<h2 mat-dialog-title>Importar planilha</h2>
<button type="button" mat-icon-button class="botao-fechar" matTooltip="Fechar" aria-label="Fechar"
    [disabled]="isGravando || isConferindo" (click)="cancelar()">
    <mat-icon>close</mat-icon>
</button>

<mat-dialog-content>
    <ol class="etapas">
        @for (etapa of listaEtapa; track etapa.id) {
            <li class="etapa"
                [class.etapa-atual]="etapa.id === etapaAtual && etapaAtual !== etapaEnum.Concluir"
                [class.etapa-feita]="etapa.id < etapaAtual || etapaAtual === etapaEnum.Concluir">
                <span class="etapa-numero">
                    @if (etapa.id < etapaAtual || etapaAtual === etapaEnum.Concluir) {
                        <span class="material-symbols-outlined">check</span>
                    } @else {
                        {{ etapa.id }}
                    }
                </span>
                <span>{{ etapa.descricao }}</span>
            </li>
        }
    </ol>

    <!-- Etapa 1: Enviar arquivos -->
    @if (etapaAtual === etapaEnum.EnviarArquivos) {
        <p class="texto-etapa">Use quando a API do IBPT estiver fora do ar ou quando for preciso carregar a tabela manualmente. Nada é gravado antes de você conferir.</p>

        <div class="row">
            <div class="col-12 col-lg-8">
                <div class="area-envio" [class.area-envio-arrastando]="isArrastando" role="button" tabindex="0"
                    (click)="inputArquivo.click()" (keydown.enter)="inputArquivo.click()"
                    (dragover)="arrastarSobre($event)" (dragleave)="isArrastando = false" (drop)="soltarArquivos($event)">
                    <span class="material-symbols-outlined">upload_file</span>
                    <p class="area-envio-titulo">Arraste os arquivos CSV aqui ou clique para escolher</p>
                    <p class="texto-apoio">Pode enviar vários de uma vez (um arquivo por UF)</p>
                </div>
                <input #inputArquivo type="file" accept=".csv" multiple hidden (change)="escolherArquivos($event)" />

                <div class="lista-arquivo">
                    @for (arquivo of listaArquivo; track arquivo.id) {
                        <div class="linha-arquivo">
                            @switch (arquivo.idStatus) {
                                @case (statusArquivoEnum.Lendo) {
                                    <span class="material-symbols-outlined icone-arquivo">hourglass_top</span>
                                }
                                @case (statusArquivoEnum.Valido) {
                                    <span class="material-symbols-outlined icone-arquivo icone-valido">check_circle</span>
                                }
                                @case (statusArquivoEnum.Recusado) {
                                    <span class="material-symbols-outlined icone-arquivo icone-recusado">error</span>
                                }
                            }

                            <div class="arquivo-conteudo">
                                <p class="arquivo-nome">
                                    {{ arquivo.nomeArquivo }}
                                    @switch (arquivo.idStatus) {
                                        @case (statusArquivoEnum.Lendo) { <span class="arquivo-status">Lendo</span> }
                                        @case (statusArquivoEnum.Valido) { <span class="arquivo-status">Válido</span> }
                                        @case (statusArquivoEnum.Recusado) { <span class="arquivo-status">Recusado</span> }
                                    }
                                </p>
                                @if (arquivo.idStatus === statusArquivoEnum.Valido) {
                                    <p class="texto-apoio">versão {{ arquivo.versao }} · {{ arquivo.quantidadeLinhas | number }} linhas ({{ arquivo.quantidadeNcm | number }} NCM · {{ arquivo.quantidadeNbs | number }} NBS · {{ arquivo.quantidadeLc116 | number }} LC 116)</p>
                                }
                                @if (arquivo.idStatus === statusArquivoEnum.Recusado) {
                                    <p class="texto-erro">{{ arquivo.motivoRecusa }}</p>
                                }
                                @for (conflito of arquivo.listaConflito; track conflito) {
                                    <p class="texto-erro">{{ conflito }}</p>
                                }
                            </div>

                            @if (arquivo.idStatus === statusArquivoEnum.Valido) {
                                <div class="campo-uf">
                                    <app-select-pesquisavel label="UF" [control]="arquivo.idUnidadeFederativaControl"
                                        [offlineList]="listaUnidadeFederativa" [idField]="'idUnidadeFederativa'" [nameField]="'descricaoExibicao'"
                                        (selectionChange)="alterarUf()">
                                    </app-select-pesquisavel>
                                </div>
                            }

                            <button type="button" mat-icon-button matTooltip="Remover" aria-label="Remover" (click)="removerArquivo(arquivo)">
                                <mat-icon>close</mat-icon>
                            </button>
                        </div>
                    }
                </div>
            </div>

            <div class="col-12 col-lg-4">
                <aside class="painel-ajuda">
                    <h3 class="secao-titulo">Como obter a planilha</h3>
                    <ol>
                        <li>Acesse <a class="btn-link" href="https://deolhonoimposto.ibpt.org.br" target="_blank" rel="noopener">deolhonoimposto.ibpt.org.br</a> e entre com a conta da ATS.</li>
                        <li>Em <b>Gerenciar empresa</b>, baixe a tabela de cada UF em CSV.</li>
                        <li>Envie os arquivos aqui, sem renomear (ex.: <b>TabelaIBPTaxMG26.2.B.csv</b>) e sem abrir no Excel.</li>
                    </ol>
                    <div class="aviso aviso-info">
                        <span class="material-symbols-outlined">info</span>
                        <p>Os arquivos são validados na próxima etapa. Linhas com problema são listadas e não impedem as demais.</p>
                    </div>
                </aside>
            </div>
        </div>
    }

    <!-- Etapa 2: Conferir -->
    @if (etapaAtual === etapaEnum.Conferir && validacao) {
        <p class="texto-etapa">Confira o que será carregado. Nada foi gravado ainda.</p>

        <section class="resumo">
            <div>
                <p class="campo-label">Versão da planilha</p>
                <p class="resumo-valor">{{ validacao.versao ?? '—' }}</p>
                <p class="campo-apoio">{{ validacao.versaoEmUso ? 'em uso: ' + validacao.versaoEmUso : 'nenhuma versão em uso' }}</p>
            </div>
            <div>
                <p class="campo-label">Vigência</p>
                <p class="resumo-valor resumo-valor-texto">{{ formatarData(validacao.dataVigenciaInicio) }} a {{ formatarData(validacao.dataVigenciaFim) }}</p>
            </div>
            <div>
                <p class="campo-label">Arquivos</p>
                <p class="resumo-valor">{{ validacao.listaArquivo.length }}</p>
                <p class="campo-apoio">{{ listaSiglaUfConferida.join(', ') }}</p>
            </div>
            <div>
                <p class="campo-label">Linhas válidas</p>
                <p class="resumo-valor">{{ validacao.quantidadeLinhasValidas | number }}</p>
                <p class="campo-apoio">de {{ validacao.quantidadeLinhasLidas | number }} lidas</p>
            </div>
            <div>
                <p class="campo-label">Linhas rejeitadas</p>
                <p class="resumo-valor" [class.texto-alerta]="validacao.quantidadeLinhasRejeitadas > 0">{{ validacao.quantidadeLinhasRejeitadas | number }}</p>
                <p class="campo-apoio">não serão importadas</p>
            </div>
        </section>

        <div class="row">
            <div class="col-12 col-lg-5">
                <section class="bloco">
                    <h3 class="secao-titulo">O que vai acontecer</h3>
                    <div class="linha-impacto">
                        <span>Itens novos</span>
                        <b>{{ validacao.quantidadeItensNovos | number }}</b>
                    </div>
                    <div class="linha-impacto">
                        <span>Itens atualizados</span>
                        <b>{{ validacao.quantidadeItensAtualizados | number }}</b>
                    </div>
                    <div class="linha-impacto">
                        <span>Itens que saem da tabela <span class="texto-apoio">(ficam desativados, não são apagados)</span></span>
                        <b>{{ validacao.quantidadeItensDesativados | number }}</b>
                    </div>

                    @if (mensagemComparacao) {
                        @if (isAvisoComparacao) {
                            <div class="aviso aviso-alerta">
                                <span class="material-symbols-outlined">warning</span>
                                <p>{{ mensagemComparacao }}</p>
                            </div>
                        } @else {
                            <div class="aviso aviso-info">
                                <span class="material-symbols-outlined">info</span>
                                <p>{{ mensagemComparacao }}</p>
                            </div>
                        }
                    }

                    @if (validacao.isConfirmacaoObrigatoria) {
                        <mat-checkbox [(ngModel)]="isConfirmado">{{ textoConfirmacao }}</mat-checkbox>
                    }

                    <p class="texto-apoio">A carga ficará registrada no histórico em nome de <b>{{ nomeUsuario }}</b>, em {{ datetimeConferencia | date:'dd/MM/yyyy' }} às {{ datetimeConferencia | date:'HH:mm' }}.</p>
                </section>
            </div>

            <div class="col-12 col-lg-7">
                <section class="bloco">
                    <h3 class="secao-titulo">Linhas rejeitadas</h3>
                    @if (validacao.listaLinhaRejeitada.length > 0) {
                        <div class="tabela-rejeitada">
                            <table mat-table [dataSource]="listaLinhaRejeitadaExibida">
                                <ng-container matColumnDef="nomeArquivo">
                                    <th mat-header-cell *matHeaderCellDef> Arquivo </th>
                                    <td mat-cell *matCellDef="let linha"> {{ linha.nomeArquivo }} </td>
                                </ng-container>

                                <ng-container matColumnDef="numeroLinha">
                                    <th mat-header-cell *matHeaderCellDef> Linha </th>
                                    <td mat-cell *matCellDef="let linha"> {{ linha.numeroLinha | number }} </td>
                                </ng-container>

                                <ng-container matColumnDef="codigo">
                                    <th mat-header-cell *matHeaderCellDef> Código </th>
                                    <td mat-cell *matCellDef="let linha"> {{ linha.codigo || '—' }} </td>
                                </ng-container>

                                <ng-container matColumnDef="motivo">
                                    <th mat-header-cell *matHeaderCellDef> Motivo </th>
                                    <td mat-cell *matCellDef="let linha"> {{ linha.motivo }} </td>
                                </ng-container>

                                <tr mat-header-row *matHeaderRowDef="displayedColumnsRejeitada"></tr>
                                <tr mat-row *matRowDef="let row; columns: displayedColumnsRejeitada;"></tr>
                            </table>
                        </div>

                        <div class="rodape-rejeitada">
                            <span class="texto-apoio">Mostrando {{ listaLinhaRejeitadaExibida.length | number }} de {{ validacao.listaLinhaRejeitada.length | number }}</span>
                            <div class="rodape-rejeitada-acoes">
                                @if (!isUltimaPaginaRejeitada) {
                                    <button type="button" mat-button class="btn-secundary" (click)="verMaisRejeitadas()">
                                        <span class="material-symbols-outlined">keyboard_arrow_down</span> Ver mais
                                    </button>
                                }
                                <button type="button" mat-button class="btn-secundary" (click)="baixarListaCompleta()">
                                    <span class="material-symbols-outlined">download</span> Baixar lista completa
                                </button>
                            </div>
                        </div>
                    } @else {
                        <p class="texto-apoio">Todas as linhas dos arquivos são válidas.</p>
                    }
                </section>
            </div>
        </div>
    }

    <!-- Etapa 3: Concluir -->
    @if (etapaAtual === etapaEnum.Concluir) {
        <div class="conclusao-cabecalho">
            <span class="material-symbols-outlined icone-conclusao">check_circle</span>
            <div>
                <h3 class="conclusao-titulo">Importação concluída</h3>
                <p class="conclusao-texto">A tabela está na versão <b>{{ versaoImportada }}</b>. A carga foi registrada no histórico em nome de <b>{{ nomeUsuario }}</b>, em {{ datetimeConclusao | date:'dd/MM/yyyy' }} às {{ datetimeConclusao | date:'HH:mm' }}.</p>
            </div>
        </div>

        <section class="resumo resumo-conclusao">
            <div>
                <p class="campo-label">Versão</p>
                <p class="resumo-valor">{{ versaoImportada }}</p>
            </div>
            <div>
                <p class="campo-label">Arquivos</p>
                <p class="resumo-valor">{{ listaResultadoUf.length }}</p>
                <p class="campo-apoio">{{ siglasUfImportadas }}</p>
            </div>
            <div>
                <p class="campo-label">Linhas importadas</p>
                <p class="resumo-valor">{{ quantidadeLinhasImportadas | number }}</p>
            </div>
            <div>
                <p class="campo-label">Linhas rejeitadas</p>
                <p class="resumo-valor" [class.texto-alerta]="quantidadeLinhasRejeitadasImportacao > 0">{{ quantidadeLinhasRejeitadasImportacao | number }}</p>
                <p class="campo-apoio">não foram importadas</p>
            </div>
        </section>

        <div class="row">
            <div class="col-12 col-lg-7">
                <section class="bloco">
                    <h3 class="secao-titulo">Arquivos importados</h3>
                    <div class="tabela-rejeitada">
                        <table mat-table [dataSource]="listaResultadoUf">
                            <ng-container matColumnDef="nomeArquivo">
                                <th mat-header-cell *matHeaderCellDef> Arquivo </th>
                                <td mat-cell *matCellDef="let resultadoUf" class="nome-arquivo-resultado"> {{ resultadoUf.nomeArquivo }} </td>
                            </ng-container>

                            <ng-container matColumnDef="idUnidadeFederativa">
                                <th mat-header-cell *matHeaderCellDef> UF </th>
                                <td mat-cell *matCellDef="let resultadoUf"> {{ siglaUf(resultadoUf.idUnidadeFederativa) }} </td>
                            </ng-container>

                            <ng-container matColumnDef="quantidadeLinhasValidas">
                                <th mat-header-cell *matHeaderCellDef> Importadas </th>
                                <td mat-cell *matCellDef="let resultadoUf"> {{ resultadoUf.quantidadeLinhasValidas | number }} </td>
                            </ng-container>

                            <ng-container matColumnDef="quantidadeLinhasRejeitadas">
                                <th mat-header-cell *matHeaderCellDef> Rejeitadas </th>
                                <td mat-cell *matCellDef="let resultadoUf"> {{ resultadoUf.quantidadeLinhasRejeitadas | number }} </td>
                            </ng-container>

                            <ng-container matColumnDef="resultado">
                                <th mat-header-cell *matHeaderCellDef> Resultado </th>
                                <td mat-cell *matCellDef="let resultadoUf">
                                    @switch (resultadoPorUf[siglaUf(resultadoUf.idUnidadeFederativa)]) {
                                        @case ('concluida') {
                                            <span class="chip-situacao chip-sucesso"><span class="material-symbols-outlined">check_circle</span>Concluída</span>
                                        }
                                        @case ('parcial') {
                                            <span class="chip-situacao chip-alerta"><span class="material-symbols-outlined">warning</span>Parcial</span>
                                        }
                                        @default {
                                            <span class="chip-situacao chip-erro"><span class="material-symbols-outlined">error</span>Falhou</span>
                                        }
                                    }
                                </td>
                            </ng-container>

                            <tr mat-header-row *matHeaderRowDef="displayedColumnsResultadoUf"></tr>
                            <tr mat-row *matRowDef="let row; columns: displayedColumnsResultadoUf;"></tr>
                        </table>
                    </div>
                </section>
            </div>

            <div class="col-12 col-lg-5">
                <section class="bloco">
                    <h3 class="secao-titulo">Resultado por UF</h3>
                    <app-mapa-uf-ibpt class="conclusao-mapa" [situacaoPorUf]="resultadoPorUf"></app-mapa-uf-ibpt>
                </section>
            </div>
        </div>
    }
</mat-dialog-content>

<mat-dialog-actions align="end">
    @switch (etapaAtual) {
        @case (etapaEnum.EnviarArquivos) {
            <button type="button" mat-flat-button class="btn-secundary" [disabled]="isConferindo" (click)="cancelar()">Cancelar</button>
            <span [matTooltip]="motivoBloqueioConferir" [matTooltipDisabled]="!motivoBloqueioConferir || isConferindo">
                <button type="button" mat-flat-button class="btn-primary" [disabled]="!!motivoBloqueioConferir || isConferindo"
                    [attr.aria-label]="'Conferir arquivos'" (click)="conferir()">
                    @if (isConferindo) {
                        <mat-spinner [diameter]="20" class="mat-spinner-btn"></mat-spinner>
                    } @else {
                        Conferir arquivos <span class="material-symbols-outlined">arrow_forward</span>
                    }
                </button>
            </span>
        }
        @case (etapaEnum.Conferir) {
            <button type="button" mat-flat-button class="btn-secundary" [disabled]="isGravando" (click)="cancelar()">Cancelar</button>
            <button type="button" mat-flat-button class="btn-secundary" [disabled]="isGravando" (click)="voltar()">
                <span class="material-symbols-outlined">arrow_back</span> Voltar
            </button>
            <span matTooltip="Marque a confirmação para importar." [matTooltipDisabled]="isPodeConfirmar">
                <button type="button" mat-flat-button class="btn-primary" [disabled]="!isPodeConfirmar || isGravando" (click)="confirmarImportacao()">
                    <span class="material-symbols-outlined">check</span> Confirmar importação
                </button>
            </span>
        }
        @case (etapaEnum.Concluir) {
            <button type="button" mat-flat-button class="btn-secundary" (click)="importarOutrosArquivos()">Importar outros arquivos</button>
            <button type="button" mat-flat-button class="btn-primary" (click)="verNoHistorico()">
                <span class="material-symbols-outlined">history</span> Ver no histórico
            </button>
        }
    }
</mat-dialog-actions>

<!-- Fora do mat-dialog-content: cobre o diálogo inteiro (a superfície do diálogo é a referência da posição). -->
@if (isGravando) {
    <div class="bloqueio-gravacao">
        <div class="bloqueio-gravacao-conteudo">
            <mat-spinner [diameter]="24"></mat-spinner>
            <div class="bloqueio-gravacao-texto">
                @if (isEnviandoArquivos) {
                    <span>Enviando arquivos… {{ percentualEnvio }}%</span>
                    <mat-progress-bar mode="determinate" [value]="percentualEnvio"></mat-progress-bar>
                    <small>Não feche esta janela até o fim.</small>
                } @else {
                    <span>{{ textoGravacao }}</span>
                    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                    <small>{{ textoTempoGravacao }}. Não feche esta janela até o fim.</small>
                }
            </div>
        </div>
    </div>
}
